/**
 * Shared progress types for agent progress tracking.
 * Used by both Deep Research and Data Capture agents.
 */

export enum ToolCallStatus {
  /** Realtime snapshot not received yet; execution phase unknown. */
  UNKNOWN = 'unknown',
  /** Waiting to start in the current batch. */
  QUEUED = 'queued',
  /** Currently executing. */
  RUNNING = 'running',
  /** Finished with a result. */
  COMPLETED = 'completed',
}

/** Suffix for tools not started yet in the current batch, e.g. "Running code (queued)". */
export const QUEUED_TOOL_DISPLAY_LABEL = '(queued)';

/**
 * Represents a tool call in progress or completed.
 * This is the base interface used by both Deep Research and Data Capture.
 */
export interface ProgressToolCall {
  /** The tool name (can be an enum value or string for external tools) */
  tool: string;
  /** Unique identifier for this tool call */
  toolId: string;
  /** When the tool call was initiated */
  createdAt: Date;
  /** Current status of the tool call for progress rendering. */
  status: ToolCallStatus;
  /** Human-readable display name for the tool */
  displayName: string;
  /** Optional tool arguments (used by Data Capture for detail display) */
  args?: Record<string, unknown>;
  /** Optional response text from the tool (used by Data Capture for detail display) */
  responseText?: string;
}

/**
 * Progress data containing tool call history.
 * Used for both in-progress and completed thinking sections.
 */
export interface ProgressData {
  toolCalls: Array<ProgressToolCall>;
}
